import { useMemo } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowSquareOut, MagnifyingGlass, Lightning } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { MarkdownRenderer } from '~/components/markdown-renderer';
import { Skeleton } from '~/components/ui/skeleton';
import { useSkillRunStatus } from '~/hooks/queries/use-inspector';
import { useResearchByRunId, useResearchCitations } from '~/hooks/queries/use-research';
import { useDocumentDetail } from '~/hooks/queries/use-documents';
import { api } from '~/lib/api-client';
import { llmKeys } from '~/lib/query-keys';
import { modelHeadline } from '~/components/model-badges';
import type { ModelsResponse } from '~/components/model-combobox';
import { skillRunModelLine } from './skill-run-model-line';

interface SkillResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string;
  sessionId?: string;
  skillSlug?: string;
  /** Speaking headline for the run (e.g. the plan title) — used when there's
   *  no research topic to show, so an action-task reads as its task, not the
   *  raw "execute-plan" slug. */
  title?: string;
  /** Fallback startedAt from the row (epoch ms) when status hasn't yet
   *  loaded a string startedAt. Lets the Started line render immediately. */
  startedAtFallback?: number;
}

// Render a timestamp in the viewer's own locale and timezone (browser
// defaults) — never a hardcoded de-DE / Europe/Berlin, which showed every
// tenant one dev's locale regardless of where they are (BUG-012).
function formatTimestamp(iso: string | number | undefined): string {
  if (iso === undefined) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const seconds = Math.round(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

/**
 * The completed (green) counterpart to SkillFailureDialog. A grounded run on
 * an attended session (deep research included — FRONT-1500) now also lands
 * as a message in the chat that started it, in addition to the inbox (the
 * saved briefing document); this dialog remains a second way to read the
 * same result without leaving the Inspector. Clicking the green card opens
 * the briefing here, expanded, so the user can read the result the moment it
 * lands — the same content they'd see by opening the entry in the inbox (or
 * the chat).
 */
export function SkillResultDialog({
  open,
  onOpenChange,
  runId,
  skillSlug,
  title,
  startedAtFallback,
}: SkillResultDialogProps) {
  const { data: status, isLoading: statusLoading } = useSkillRunStatus(
    runId,
    open,
  );
  // Resolve THIS run by its own skillRunId (not the session's latest), so an
  // older stacked card opens its own briefing — not whatever ran most recently.
  const { data: run } = useResearchByRunId(open ? runId : undefined);
  const matchingRun = run && run.skillRunId === runId ? run : null;
  const { data: citations } = useResearchCitations(matchingRun?.id, {
    enabled: open && !!matchingRun,
  });

  const isDeepResearch =
    skillSlug === 'deep-research' || status?.skillSlug === 'deep-research';
  // Action-task (FRONT-503): the execute-plan acting route produces no research
  // briefing — its result is the agent's short summary, held in the run's
  // tracked `output`. We surface that here and offer a deep link to the
  // Schedule page (the action is usually creating a scheduled task).
  const isActionTask =
    skillSlug === 'execute-plan' || status?.skillSlug === 'execute-plan';
  const TypeIcon = isDeepResearch ? MagnifyingGlass : Lightning;

  const startedIso = status?.startedAt;
  const completedIso = status?.completedAt;

  const startedDisplay = useMemo(() => {
    if (startedIso) return formatTimestamp(startedIso);
    if (typeof startedAtFallback === 'number') return formatTimestamp(startedAtFallback);
    return '';
  }, [startedIso, startedAtFallback]);

  const completedDisplay = useMemo(
    () => (completedIso ? formatTimestamp(completedIso) : ''),
    [completedIso],
  );

  const durationDisplay = useMemo(() => {
    const startMs = startedIso ? new Date(startedIso).getTime() : startedAtFallback;
    const endMs = completedIso ? new Date(completedIso).getTime() : null;
    if (!startMs || !endMs) return '';
    return formatDuration(endMs - startMs);
  }, [startedIso, startedAtFallback, completedIso]);

  // FRONT-2260: "Ran on <model>" — only once the run is no longer running
  // (a mid-run model can still change via a runtime fallback) and only when
  // the row actually recorded one (older runs didn't). Query key matches
  // ModelCombobox's, so this rides the same cache with no extra request
  // when the list is already loaded elsewhere on the page.
  const { data: modelsData } = useQuery({
    queryKey: llmKeys.models(),
    queryFn: () => api.get<ModelsResponse>('/api/llm/models'),
    staleTime: 5 * 60_000,
    enabled: open && !!status?.model,
  });
  const nameOf = (alias: string) => {
    const entry = modelsData?.models.find((m) => m.id === alias);
    return entry ? modelHeadline(entry) : alias;
  };
  const modelLine =
    status?.status && status.status !== 'running' ? skillRunModelLine(status, nameOf) : null;

  const sourceCount = citations?.length ?? 0;
  const briefingFilename = matchingRun?.briefingDocFilename;
  // Fetch the briefing as soon as the dialog opens — the whole point of this
  // modal is to show the result immediately, so it renders expanded by default.
  const { data: briefingDoc, isLoading: briefingLoading } = useDocumentDetail(
    briefingFilename ?? null,
    { enabled: open && !!briefingFilename },
  );
  // When there's no saved briefing (any non-research skill, e.g. an
  // action-task), the result IS the run's final summary, tracked as `output`.
  const resultText = status?.output?.trim() ?? '';
  // Show the skeleton until we actually know what to render. The status query
  // is disabled on a settled row, so the dialog is the first consumer to enable
  // it — on first open `status` is undefined and `resultText` is '', which would
  // otherwise flash the "No result summary" empty-state before the run's output
  // arrives. The status endpoint always responds 200 (never retries), so this
  // never stalls. (We deliberately don't gate on the research query: its
  // endpoint can 404/retry for action-tasks and would stall the card.)
  const contentLoading = briefingLoading || statusLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* sm:max-w-3xl overrides shadcn's default sm:max-w-sm so the briefing is
       *  readable on desktop. */}
      <DialogContent className="sm:max-w-3xl">
        {/* min-w-0 on the grid child (header) AND the flex row (title) lets the
         *  truncate on the title span actually engage — without it, a long topic
         *  blows the grid column past the modal and shoves the badge + close
         *  button off-screen. */}
        <DialogHeader className="min-w-0">
          <DialogTitle className="flex items-center gap-2 min-w-0">
            <TypeIcon size={18} className="text-muted-foreground shrink-0" />
            <span className="text-base font-medium truncate">
              {matchingRun?.topic || title || status?.skillSlug || skillSlug || 'skill'}
            </span>
            <Badge variant="success" className="ml-1 shrink-0">Completed</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 text-sm min-w-0">
          {(startedDisplay || completedDisplay || durationDisplay) && (
            <section className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              {startedDisplay && (
                <>
                  <span className="text-muted-foreground">Started</span>
                  <span className="tabular-nums">{startedDisplay}</span>
                </>
              )}
              {completedDisplay && (
                <>
                  <span className="text-muted-foreground">Completed</span>
                  <span className="tabular-nums">{completedDisplay}</span>
                </>
              )}
              {durationDisplay && (
                <>
                  <span className="text-muted-foreground">Duration</span>
                  <span className="tabular-nums">{durationDisplay}</span>
                </>
              )}
              {matchingRun && (
                <>
                  <span className="text-muted-foreground">Sources</span>
                  <span className="tabular-nums">
                    {sourceCount} {sourceCount === 1 ? 'source' : 'sources'}
                  </span>
                </>
              )}
            </section>
          )}

          {modelLine && (
            <p data-testid="skill-run-model" className="text-xs text-muted-foreground">
              {modelLine}
            </p>
          )}

          <section>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground/70">
                Result
              </div>
              {briefingFilename ? (
                <Link
                  to={`/files/doc/${encodeURIComponent(briefingFilename)}`}
                  onClick={() => onOpenChange(false)}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowSquareOut size={11} />
                  Open full page
                </Link>
              ) : isActionTask ? (
                <Link
                  to="/crons"
                  onClick={() => onOpenChange(false)}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowSquareOut size={11} />
                  View scheduled tasks
                </Link>
              ) : null}
            </div>
            {/* min-w-0 + break-words: long source URLs / headings in the
             *  briefing wrap instead of forcing the grid column (and the whole
             *  modal) wider than max-w-3xl. overflow-auto still scrolls wide
             *  tables/code blocks within the box. */}
            <div className="rounded border border-border bg-card/40 p-3 max-h-[60vh] overflow-auto min-w-0 break-words [overflow-wrap:anywhere]">
              {contentLoading ? (
                <Skeleton className="h-40 w-full" />
              ) : briefingDoc ? (
                <MarkdownRenderer content={briefingDoc.content} />
              ) : briefingFilename ? (
                // A research run with a saved briefing whose doc didn't load
                // (transient 404 / not-yet-indexed / deleted): keep pointing at
                // the inbox rather than dumping the raw, 50k-capped `output`.
                <div className="text-sm text-muted-foreground italic">
                  The result is in your inbox. Open the full page to read it.
                </div>
              ) : resultText ? (
                <MarkdownRenderer content={resultText} />
              ) : (
                <div className="text-sm text-muted-foreground italic">
                  No result summary was recorded for this run.
                </div>
              )}
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
